/**
 * Error Boundary component for catching React errors
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 * 
 * Integrates with Sentry for error tracking if available.
 * 
 * Reference:
 * - React Error Boundaries: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 * - Sentry React Integration: https://docs.sentry.io/platforms/javascript/guides/react/
 */

import {Component, type ReactNode} from 'react';
import {Button} from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * Whether to report errors to Sentry
   * @default true
   */
  reportToSentry?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error: Error): State {
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Always log to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Report to Sentry if enabled
    const shouldReport = this.props.reportToSentry !== false;
    if (shouldReport) {
      this.reportToSentry(error, errorInfo);
    }
  }

  /**
   * Report error to Sentry if available
   * Uses graceful degradation - works whether Sentry is installed or not
   * 
   * Reference: Sentry React Error Boundary
   * https://docs.sentry.io/platforms/javascript/guides/react/components/error-boundary/
   */
  private reportToSentry(error: Error, errorInfo: React.ErrorInfo) {
    // Use dynamic import with a try-catch to avoid breaking if Sentry isn't installed
    // This allows graceful degradation
    // Using a function to prevent Vite from trying to resolve the import at build time
    const tryImportSentry = async () => {
      try {
        // Use a string literal that's constructed to avoid static analysis
        const sentryModule = '@sentry/react';
        const Sentry = await import(/* @vite-ignore */ sentryModule);
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
          tags: {
            errorBoundary: true,
          },
        });
      } catch (importError) {
        // Sentry not available - this is okay, error already logged to console
        // Only log in development to avoid production noise
        if (process.env.NODE_ENV === 'development') {
          console.debug('Sentry not available - error not reported to Sentry');
        }
      }
    };
    
    // Call async function without awaiting (fire and forget)
    tryImportSentry();
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  this.setState({hasError: false, error: null});
                  window.location.reload();
                }}
              >
                Reload Page
              </Button>
              <div>
                <button
                  onClick={() => window.history.back()}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
                >
                  Go back
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

